// trackPostView.js
// Helper function to track post views in post_insights table
import { supabase } from '../supabase/supabase';

/**
 * Track a post view in the post_insights table
 * @param {string} postId - The ID of the post being viewed
 * @param {string} viewerId - The ID of the user viewing the post
 * @returns {Promise<void>}
 */
export async function trackPostView(postId, viewerId) {
  if (!postId || !viewerId) {
    console.warn('[trackPostView] Missing postId or viewerId');
    return;
  }

  try {
    // Get post owner to check if viewer is the owner
    const { data: post, error: postError } = await supabase
      .from('posts')
      .select('user_id')
      .eq('id', postId)
      .single();

    if (postError || !post) {
      console.warn('[trackPostView] Post not found:', postError);
      return;
    }

    // Don't track views for the post owner
    if (post.user_id === viewerId) {
      return;
    }

    // Check if viewer is a follower
    const { data: followData } = await supabase
      .from('follows')
      .select('id')
      .eq('follower_id', viewerId)
      .eq('following_id', post.user_id)
      .single();

    const isFollower = !!followData;

    // Check if view already exists for this user and post
    const { data: existingView } = await supabase
      .from('post_insights')
      .select('id')
      .eq('post_id', postId)
      .eq('viewer_id', viewerId)
      .eq('interaction_type', 'view')
      .single();

    // If view already exists, don't insert again (one view per user)
    if (existingView) {
      return;
    }

    // Insert view insight (only if it doesn't exist)
    const { error: insightError } = await supabase
      .from('post_insights')
      .insert({
        post_id: postId,
        viewer_id: viewerId,
        is_follower: isFollower,
        interaction_type: 'view',
        created_at: new Date().toISOString()
      });

    if (insightError) {
      // If it's a duplicate error, that's okay - we just ignore it
      if (insightError.code !== '23505') {
        console.warn('[trackPostView] Error tracking view:', insightError);
      }
    }
  } catch (error) {
    console.warn('[trackPostView] Failed to track view:', error);
    // Fail silently - view tracking shouldn't break the app
  }
}

/**
 * Track post view using RPC function (if available)
 * This is more efficient as it handles follower check server-side
 * @param {string} postId - The ID of the post being viewed
 * @param {string} viewerId - The ID of the user viewing the post
 * @returns {Promise<void>}
 */
export async function trackPostViewRPC(postId, viewerId) {
  if (!postId || !viewerId) return;

  try {
    const { error } = await supabase.rpc('upsert_post_insight', {
      p_post_id: postId,
      p_viewer_id: viewerId,
      p_interaction_type: 'view'
    });

    if (error && error.code !== 'P0001') { // P0001 is "no rows returned" which is fine
      console.warn('[trackPostViewRPC] Error:', error);
    }
  } catch (error) {
    console.warn('[trackPostViewRPC] Failed:', error);
  }
}

